import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import { Badge, PopoverBody, UncontrolledPopover } from 'reactstrap';
import { DealerStatusTypes } from '../../../../shared/dealer';
import * as OrderSummary from '../../../../shared/orders/model';
import { isMinorAttendee, UserRegistration } from '../../../../shared/user/base';
import { MaterialIcon } from '../../../components';
import { DateT } from '../../../components/Date';
import { useTranslation } from '../../../translations';
import { displayName, useConvention } from '../../../utils';

export const OrderBadges: FC<{ readonly order: OrderSummary.Order }> = ({ order }) => {
  const { ts } = useTranslation();
  const prefix = `order${order.id}`;

  return order.status === 'paid' ? (
    <Badge color="success" id={`${prefix}Paid`}>
      {ts('paid_in_full')}
    </Badge>
  ) : (
    <Badge color="danger" id={`${prefix}Unpaid`}>
      {ts('common.unpaid')}
    </Badge>
  );
};

export const RegistrationAttendanceType: FC<{ readonly registration: UserRegistration }> = ({
  registration,
}) => {
  const { ts } = useTranslation();
  if (registration.paidOrderItem) {
    return (
      <span id="attendanceType">
        {displayName(registration.paidOrderItem)}
        <Link to={`/housekeeping/orders/${registration.paidOrderItem.orderId}`}>
          <span className="inline-icon" id="attendanceTypePaid">
            <MaterialIcon name="done" type="success" />
          </span>
          <UncontrolledPopover placement="bottom" target="attendanceTypePaid" trigger="hover">
            <PopoverBody>
              {ts('this_user_has_an_active_entitlement_that_grants_this_attendance')}
            </PopoverBody>
          </UncontrolledPopover>
        </Link>
      </span>
    );
  }

  return (
    <span id="attendanceType">
      {displayName(registration.attendanceType)}
      <span className="inline-icon" id="attendanceTypeMismatch">
        <MaterialIcon name="warning" type="warning" />
      </span>
      <UncontrolledPopover placement="bottom" target="attendanceTypeMismatch" trigger="hover">
        <PopoverBody>
          <p>{ts('this_user_does_not_have')}</p>
        </PopoverBody>
      </UncontrolledPopover>
    </span>
  );
};

export const DealerApplicationStatus: FC<{
  readonly status: DealerStatusTypes;
  readonly grant: boolean;
}> = ({ status, grant }) => {
  const { ts } = useTranslation();
  if (status === 'pending' || status === 'cancelled') {
    return status;
  }

  return (
    <>
      {status}
      {grant ? (
        <>
          <span className="inline-icon" id="dealerPaid">
            <MaterialIcon name="done" type="success" />
          </span>
          <UncontrolledPopover placement="bottom" target="dealerPaid" trigger="hover">
            <PopoverBody>{ts('this_dealer_has_paid')}</PopoverBody>
          </UncontrolledPopover>
        </>
      ) : (
        <>
          <span className="inline-icon" id="dealerUnpaid">
            <MaterialIcon name="warning" type="warning" />
          </span>
          <UncontrolledPopover placement="bottom" target="dealerUnpaid" trigger="hover">
            <PopoverBody>{ts('this_dealer_has_not_yet')}</PopoverBody>
          </UncontrolledPopover>
        </>
      )}
    </>
  );
};

interface UserBadgesProps {
  readonly user: {
    id: number;
    owner?: { id: number };
    permissions?: string[];
    pendingReview?: boolean;
    hasRestrictionNotice: boolean;
    hasBanNotice: boolean;
    isPriorYearAttendee?: boolean | null;
    bornAt?: Date | null;
    registration?: Pick<UserRegistration, 'id' | 'paidOrderItem'>;
    child: boolean;
  };
  readonly showBornAt?: boolean;
}

export const UserBadges: FC<UserBadgesProps> = ({ user, showBornAt }) => {
  const { ts } = useTranslation();
  const { startAt, childAgeThreshold } = useConvention();
  const badges: React.JSX.Element[] = [];

  if (user.permissions) {
    if (user.permissions.includes('system:staff')) {
      badges.push(
        <Badge color="primary" key="staff">
          {ts('staff_member')}
        </Badge>,
      );
    }

    if (user.permissions.includes('system:staffalumni')) {
      badges.push(
        <Badge color="info" key="staffAlumni">
          {ts('staff_alumni')}
        </Badge>,
      );
    }
  }

  if (user.hasBanNotice) {
    badges.push(
      <Badge color="danger" key="banned">
        {ts('banned')}
      </Badge>,
    );
  }

  if (user.owner) {
    badges.push(
      <Badge color="warning" key="subaccount">
        {ts('subaccount')}
      </Badge>,
    );
  }

  if (user.child || (user.bornAt && isMinorAttendee(user.bornAt, startAt, childAgeThreshold))) {
    badges.push(
      <Badge color="warning" key="child">
        {ts('common.child')}
      </Badge>,
    );
  }

  if (user.pendingReview) {
    badges.push(
      <>
        <Badge color="warning" id={`pendingReview${user.id}`} key="review">
          {ts('pending_review')}
        </Badge>
        <UncontrolledPopover placement="bottom" target={`pendingReview${user.id}`} trigger="hover">
          <PopoverBody>{ts('this_user_is_a_possible')}</PopoverBody>
        </UncontrolledPopover>
      </>,
    );
  }

  if (user.registration) {
    if (user.registration.paidOrderItem) {
      badges.push(
        <Badge className="registered" color="primary" key="registered">
          {ts('registered_paid')}
        </Badge>,
      );
    } else {
      badges.push(
        <Badge className="registered-not-paid" color="warning" key="registeredNotPaid">
          {ts('registered_not_paid')}
        </Badge>,
      );
    }
  } else {
    badges.push(
      <Badge className="not-registered" color="secondary" key="notRegistered">
        {ts('not_registered')}
      </Badge>,
    );
  }

  if (user.isPriorYearAttendee) {
    badges.push(
      <Badge className="prior-year-attendee" color="info" key="priorYearAttendee">
        {ts('common.returning_attendee')}
      </Badge>,
    );
  }

  if (user.hasRestrictionNotice) {
    badges.push(
      <Badge className="has-restriction-notice" color="danger" key="hasRestrictionNotice">
        {ts('common.restriction')}
      </Badge>,
    );
  }

  if (showBornAt && user.bornAt) {
    badges.push(
      <Badge
        className="has-born-at"
        color="secondary"
        key="hasBornAt"
        title={ts('common.date_of_birth')}
      >
        {ts('common.dob')}: <DateT value={user.bornAt} />
      </Badge>,
    );
  }

  return <span className="badge-list compact">{badges}</span>;
};
